<template>
    <div class="hover-bar">
        <div class="hover-bar__btn">
            <simple-svg :src="CopyIcon" width="16" height="16" custom-class-name="icon" />
        </div>
        <div class="hover-bar__rate" ref="rateBtn">
            <span v-if="selectedRating">{{ selectedRating }}</span>
            <div class="hover-bar__btn" @click="toggleRating">
                <simple-svg :src="HeartIcon" width="16" height="16" :custom-class-name="selectedRating ? 'icon-active' : 'icon'" />
            </div>
            <div v-if="showRating" class="rating-popup" :style="popupStyle">
                <div
                    v-for="n in 6"
                    :key="n - 1"
                    class="rating-popup__dot"
                    :class="{ active: selectedRating === n - 1 }"
                    @click="selectRating(n - 1)"
                >
                    {{ n - 1 }}
                </div>
            </div>
        </div>
        <span v-if="date">{{ formatDateTime(date) }}</span>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';
import CopyIcon from '@/assets/icons/Copy.svg';
import HeartIcon from '@/assets/icons/Heart_01.svg';
import { formatDateTime } from '@/utils/dateUtils';
import { useAgentStore } from '@/store/AgentStore';

const agentStore = useAgentStore();

const props = defineProps({
    messageId: { type: String, required: true },
    date: { type: String, required: false }
});

const emit = defineEmits(['rate']);

const showRating = ref(false);
const selectedRating = ref(null);
const rateBtn = ref(null);
const btnRect = ref(null);

const popupStyle = computed(() => {
    if (!btnRect.value) return {};
    return {
        position: 'fixed',
        top: `${btnRect.value.top - 40}px`,
        left: `${btnRect.value.left + btnRect.value.width / 2}px`,
        transform: 'translateX(-50%)',
    };
});

function toggleRating() {
    if (!showRating.value && rateBtn.value) {
        btnRect.value = rateBtn.value.getBoundingClientRect();
    }
    showRating.value = !showRating.value;
}

function selectRating(value) {
    selectedRating.value = value;
    showRating.value = false;
    agentStore.addRating(props.messageId, value);
    emit('rate', value);
}

function onClickOutside(e) {
    if (rateBtn.value && !rateBtn.value.contains(e.target)) {
        showRating.value = false;
    }
}

onMounted(() => document.addEventListener('click', onClickOutside));
onBeforeUnmount(() => document.removeEventListener('click', onClickOutside));
</script>

<style lang="scss" scoped>
.hover-bar {
    display: flex;
    gap: 5px;
    align-items: center;
    border-radius: 8px;
    font-size: 13px;
    color: var(--color-text-dark);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;

    .icon-active {
        path {
            color: #4f46e5;
        }
    }

    span {
        font-weight: 300;
        font-size: 12px;
        margin-left: 5px;
    }

    &__rate {
        position: relative;
        display: flex;
        align-items: center;
        gap: 5px;
    }

    &__btn {
        display: flex;
        padding: 5px;
        border-radius: 5px;
        cursor: pointer;

        &:hover {
            background-color: var(--color-bg);
        }

        &:active {
            background-color: var(--color-bg-dark);
        }
    }
}

.rating-popup {
    display: flex;
    gap: 2px;
    padding: 4px 6px;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    z-index: 10;

    &__dot {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 26px;
        height: 26px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        color: var(--color-text-dark);
        transition: background-color 0.1s ease;

        &:hover {
            background-color: var(--color-bg);
        }

        &.active {
            background-color: var(--color-blue, #4f46e5);
            color: #fff;
        }
    }
}
</style>
